import axiosInstance from './axiosInstance';
import { getPostAuthRedirectPath } from './postAuthRedirect';
import { getSignupCouponCode } from './signupCoupon';

interface GoogleConfig {
  client_id: string;
  redirect_uri: string;
  authorization_url: string;
  scope: string;
}

interface GitHubConfig {
  client_id: string;
  redirect_uri: string;
  authorization_url: string;
  scope: string;
}

interface OAuthState {
  provider: string;
  timestamp: number;
  nonce: string;
  referral_code?: string;
  coupon_code?: string;
  flow?: string;
  return_to?: string;
  project_id?: string;
}

function createStateObject(
  provider: string,
  referralCode?: string,
  extra?: Partial<OAuthState>,
  couponCode?: string,
): OAuthState {
  return {
    provider,
    timestamp: Date.now(),
    nonce: Math.random().toString(36).substring(7),
    ...(referralCode ? { referral_code: referralCode } : {}),
    ...(couponCode ? { coupon_code: couponCode } : {}),
    ...(extra || {}),
  };
}

function storeState(provider: string, state: OAuthState): void {
  sessionStorage.setItem(`oauth_state_${provider}`, JSON.stringify(state));
}

function isStateExpired(timestamp: number): boolean {
  const timeDiff = Date.now() - timestamp;

  return timeDiff > 5 * 60 * 1000;
}

function parseStatePair(provider: string, state: string): { stored: OAuthState; received: OAuthState } | null {
  const storedState = sessionStorage.getItem(`oauth_state_${provider}`);

  if (!storedState) return null;

  return { stored: JSON.parse(storedState), received: JSON.parse(atob(state)) };
}

function validateState(provider: string, state: string): boolean {
  const pair = parseStatePair(provider, state);

  if (!pair || isStateExpired(pair.received.timestamp)) return false;

  sessionStorage.removeItem(`oauth_state_${provider}`);

  return pair.stored.nonce === pair.received.nonce;
}

function buildAuthUrl(baseUrl: string, params: Record<string, string>): string {
  const queryParams = new URLSearchParams(params);

  return `${baseUrl}?${queryParams.toString()}`;
}

function requireOAuthConfig(provider: string, config: Pick<GitHubConfig, 'client_id' | 'redirect_uri'>): void {
  if (!config.client_id || !config.redirect_uri) {
    throw new Error(`${provider} OAuth is not configured. Set the client ID and callback URL first.`);
  }
}

export const oauthService = {
  generateState(provider: string, referralCode?: string, extra?: Partial<OAuthState>): string {
    const state = createStateObject(provider, referralCode, extra, getSignupCouponCode() || undefined);

    storeState(provider, state);

    return btoa(JSON.stringify(state));
  },

  generateRepoConnectState(returnTo: string, projectId?: string | null): string {
    const state = createStateObject('github_repo_connect', undefined, {
      flow: 'github_repo_connect',
      return_to: returnTo,
      ...(projectId ? { project_id: projectId } : {}),
    });

    storeState('github_repo_connect', state);

    return btoa(JSON.stringify(state));
  },

  verifyState(provider: string, state: string): boolean {
    try {
      return validateState(provider, state);
    } catch {
      return false;
    }
  },

  async getGoogleConfig(): Promise<GoogleConfig> {
    const response = await axiosInstance.get('/api/v1/auth/google/config');

    return response.data;
  },

  async getGitHubConfig(): Promise<GitHubConfig> {
    const response = await axiosInstance.get('/api/v1/auth/github/config');

    return response.data;
  },

  // Existing config fetch/fallback flow is intentionally kept together.
  // eslint-disable-next-line max-statements
  async getGoogleAuthUrl(): Promise<string> {
    try {
      const config = await this.getGoogleConfig();
      const referralCode = localStorage.getItem('referral_code') || undefined;
      const returnTo = getPostAuthRedirectPath();
      const state = this.generateState('google', referralCode, returnTo ? { return_to: returnTo } : undefined);

      const params = {
        client_id: config.client_id,
        redirect_uri: config.redirect_uri,
        response_type: 'code',
        scope: config.scope,
        state,
        access_type: 'offline',
        prompt: 'consent',
      };

      return buildAuthUrl(config.authorization_url, params);
    } catch (error) {
      console.error('Failed to fetch Google OAuth config:', error);
      // Fallback or rethrow? Rethrowing is safer to show error to user
      throw error;
    }
  },

  getAppleAuthUrl(): string {
    const clientId = process.env.REACT_APP_APPLE_CLIENT_ID || '';

    const redirectUri = process.env.REACT_APP_APPLE_REDIRECT_URI || '';

    const referralCode = localStorage.getItem('referral_code') || undefined;
    const returnTo = getPostAuthRedirectPath();
    const state = this.generateState('apple', referralCode, returnTo ? { return_to: returnTo } : undefined);

    const params = {
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code id_token',
      response_mode: 'form_post',
      scope: 'email name',
      state,
    };

    return buildAuthUrl('https://appleid.apple.com/auth/authorize', params);
  },

  getMicrosoftAuthUrl(): string {
    const clientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID || '';

    const redirectUri = process.env.REACT_APP_MICROSOFT_REDIRECT_URI || '';

    const referralCode = localStorage.getItem('referral_code') || undefined;
    const returnTo = getPostAuthRedirectPath();
    const state = this.generateState('microsoft', referralCode, returnTo ? { return_to: returnTo } : undefined);

    const params = {
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid profile email User.Read',
      state,
    };

    return buildAuthUrl('https://login.microsoftonline.com/common/oauth2/v2.0/authorize', params);
  },

  async getGitHubAuthUrl(): Promise<string> {
    const config = await this.getGitHubConfig();

    requireOAuthConfig('GitHub', config);
    const referralCode = localStorage.getItem('referral_code') || undefined;
    const returnTo = getPostAuthRedirectPath();
    const state = this.generateState('github', referralCode, returnTo ? { return_to: returnTo } : undefined);

    const params = {
      client_id: config.client_id,
      scope: config.scope || 'read:user user:email',
      state,
    };

    return buildAuthUrl(config.authorization_url || 'https://github.com/login/oauth/authorize', params);
  },

  async getGitHubRepoConnectAuthUrl(returnTo: string, projectId?: string | null): Promise<string> {
    const config = await this.getGitHubConfig();

    requireOAuthConfig('GitHub', config);
    const state = this.generateRepoConnectState(returnTo, projectId);

    const params = {
      client_id: config.client_id,
      scope: 'repo read:user user:email read:org',
      state,
      prompt: 'consent',
    };

    return buildAuthUrl(config.authorization_url || 'https://github.com/login/oauth/authorize', params);
  },

  handleOAuthCallback(provider: string, _code: string, state: string): boolean {
    if (!this.verifyState(provider, state)) {
      throw new Error('Invalid OAuth state - possible CSRF attack');
    }

    return true;
  },
};
